if (!customElements.get('set-story-slider')) {
  customElements.define('set-story-slider', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.track = this.querySelector('[data-track]');
      this.slides = [...this.querySelectorAll('[data-slide]')];
      this.dots = [...this.querySelectorAll('[data-index]')];
      this.index = 0;
      this.paused = false;
      this.busy = false;
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.track.addEventListener('scroll', () => {
        this.index = Math.round(this.track.scrollLeft / this.track.clientWidth) || 0;
        this.update();
      }, options);
      document.addEventListener('visibilitychange', () => this.start(), options);
      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail.blockId);
        if (index < 0) return;
        this.stop();
        this.go(index, true);
        this.start();
      }, options);
      document.addEventListener('shopify:block:deselect', () => {
        this.start();
      }, options);
      this.motion.addEventListener('change', () => this.start(), options);
      this.resize = new ResizeObserver(() => this.go(this.index, true));
      this.resize.observe(this.track);
      this.update();
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      this.controller.abort();
      this.resize.disconnect();
    }

    stop() {
      clearInterval(this.timer);
    }

    start() {
      this.stop();
      if (!this.isConnected || this.dataset.autoplay !== 'true' || this.slides.length < 2 ||
          this.paused || this.busy || document.hidden) return;
      this.timer = setInterval(() => this.go(this.index + 1), Number(this.dataset.interval) || 5000);
    }

    go(index, instant = false) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: instant || this.motion.matches ? 'instant' : 'smooth' });
      this.update();
    }

    update() {
      this.slides.forEach((slide, index) => {
        slide.inert = index !== this.index;
        slide.setAttribute('aria-hidden', String(index !== this.index));
      });
      this.dots.forEach((dot) => {
        if (Number(dot.dataset.index) === this.index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || !this.contains(button)) return;
      if (button.matches('[data-add-set]')) return this.addSet(button);
      if (button.matches('[data-previous]')) this.go(this.index - 1);
      if (button.matches('[data-next]')) this.go(this.index + 1);
      if (button.matches('[data-index]')) this.go(Number(button.dataset.index));
      if (button.matches('[data-pause]')) {
        this.paused = !this.paused;
        const label = this.paused ? 'Play slideshow' : 'Pause slideshow';
        this.querySelectorAll('[data-pause]').forEach((control) => {
          control.setAttribute('aria-label', label);
          control.setAttribute('title', label);
          control.setAttribute('aria-pressed', String(this.paused));
          control.querySelector('[data-pause-icon]').hidden = this.paused;
          control.querySelector('[data-play-icon]').hidden = !this.paused;
        });
      }
      this.start();
    }

    async addSet(button) {
      if (this.busy || button.disabled) return;
      const slide = button.closest('[data-slide]');
      const status = slide.querySelector('[data-status]');
      const label = button.querySelector('[data-button-label]');
      const originalLabel = label.textContent;
      const items = JSON.parse(slide.querySelector('[data-set-items]').textContent);
      if (!items.length || items.some((item) => !item.id)) return;
      this.busy = true;
      this.stop();
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      label.textContent = 'Adding...';
      status.textContent = '';
      const root = window.Shopify?.routes?.root || '/';
      const cart = document.querySelector('cart-drawer');
      const sections = cart?.getSectionsToRender().map((section) => section.id);
      let added = false;
      try {
        cart?.setActiveElement(button);
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, ...(sections ? { sections, sections_url: window.location.pathname } : {}) }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'This set could not be added. Please try again.');
        added = true;
        status.textContent = 'Set added to your bag.';
        if (cart && sections.every((id) => result.sections?.[id])) {
          cart.classList.remove('is-empty');
          await cart.renderContents(result);
          document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        } else {
          window.location.assign(`${root}cart`);
        }
      } catch (error) {
        if (added) window.location.assign(`${root}cart`);
        else status.textContent = error.message || 'Unable to add this set. Please try again.';
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
        label.textContent = originalLabel;
        this.start();
      }
    }
  });
}
